import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type ReactNode,
  type Ref,
  type SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import {
  ResponsiveGridLayout,
  useContainerWidth,
  type Layout,
  type LayoutItem,
  type ResponsiveLayouts,
} from 'react-grid-layout';
import { useBackofficePreference } from '@/preferences';
import type { BackofficePageMode } from './BackofficePageModeActionMenu';

export type PanelSpan =
  | '1x1'
  | '2x1'
  | '3x1'
  | '4x1'
  | '1x2'
  | '2x2'
  | '3x2'
  | '4x2'
  | '1x3'
  | '2x3'
  | '3x3'
  | '4x3'
  | '1x4'
  | '2x4'
  | '3x4'
  | '4x4'
  | '1x5'
  | '2x5'
  | '3x5'
  | '4x5';

const PANEL_SPAN_CLASSES: Record<PanelSpan, string> = {
  '1x1': '',
  '2x1': 'md:col-span-2',
  '3x1': 'md:col-span-3',
  '4x1': 'md:col-span-4',
  '1x2': 'md:row-span-2',
  '2x2': 'md:col-span-2 md:row-span-2',
  '3x2': 'md:col-span-3 md:row-span-2',
  '4x2': 'md:col-span-4 md:row-span-2',
  '1x3': 'md:row-span-3',
  '2x3': 'md:col-span-2 md:row-span-3',
  '3x3': 'md:col-span-3 md:row-span-3',
  '4x3': 'md:col-span-4 md:row-span-3',
  '1x4': 'md:row-span-4',
  '2x4': 'md:col-span-2 md:row-span-4',
  '3x4': 'md:col-span-3 md:row-span-4',
  '4x4': 'md:col-span-4 md:row-span-4',
  '1x5': 'md:row-span-5',
  '2x5': 'md:col-span-2 md:row-span-5',
  '3x5': 'md:col-span-3 md:row-span-5',
  '4x5': 'md:col-span-4 md:row-span-5',
};

const GRID_COLS = {
  xxl: 6,
  lg: 4,
  md: 3,
  sm: 1,
  xs: 1,
  xxs: 1,
};

const GRID_BREAKPOINTS = {
  xxl: 1536,
  lg: 1024,
  md: 768,
  sm: 640,
  xs: 480,
  xxs: 0,
};

const DETAIL_PANEL_GRID_ROW_HEIGHT = 176;
const DETAIL_PANEL_GRID_MARGIN: [number, number] = [16, 16];

type BreakpointName = keyof typeof GRID_COLS;
type DetailPanelGridCols = Partial<Record<BreakpointName, number>>;
type DetailPanelLayouts = ResponsiveLayouts<BreakpointName>;
type DetailPanelInteractionValues = Record<string, unknown>;
type DetailPanelTileSettingsPreference = {
  orderedSettingIds?: string[] | undefined;
  hiddenSettingIds?: string[] | undefined;
};
type DetailPanelGridPreference = {
  layouts?: DetailPanelLayouts | undefined;
  tileSettings?: Record<string, DetailPanelTileSettingsPreference> | undefined;
  visibleTileIds?: string[] | undefined;
};

interface DetailPanelInteractionContextValue {
  values: DetailPanelInteractionValues;
  setValues: Dispatch<SetStateAction<DetailPanelInteractionValues>>;
  tileSettings: Record<string, DetailPanelTileSettingsPreference>;
  setTileSettings: (tileId: string, settings: DetailPanelTileSettingsPreference) => void;
  removeTile: (tileId: string) => void;
  layoutMode: BackofficePageMode;
}

const DetailPanelInteractionContext = createContext<DetailPanelInteractionContextValue | null>(
  null
);

const ADD_TILE_ITEM_ID = '__backoffice-add-tile__';

export function useDetailPanelInteractionValue<T>(
  key: string
): [T | undefined, (value: T | undefined | ((current: T | undefined) => T | undefined)) => void] {
  const context = useContext(DetailPanelInteractionContext);
  const value = context?.values[key] as T | undefined;
  const setValues = context?.setValues;
  const setValue = useCallback(
    (nextValue: T | undefined | ((current: T | undefined) => T | undefined)) => {
      if (!setValues) {
        return;
      }

      setValues((currentValues) => {
        const currentValue = currentValues[key] as T | undefined;
        const resolvedValue =
          typeof nextValue === 'function'
            ? (nextValue as (current: T | undefined) => T | undefined)(currentValue)
            : nextValue;

        if (Object.is(currentValue, resolvedValue)) {
          return currentValues;
        }

        if (resolvedValue === undefined) {
          const { [key]: _removedValue, ...remainingValues } = currentValues;
          return remainingValues;
        }

        return {
          ...currentValues,
          [key]: resolvedValue,
        };
      });
    },
    [key, setValues]
  );

  return [value, setValue];
}

export function useDetailPanelTileRemoval(tileId: string): () => void {
  const context = useContext(DetailPanelInteractionContext);

  return useCallback(() => {
    context?.removeTile(tileId);
  }, [context, tileId]);
}

function formatSpan(cols: number, rows: number): PanelSpan {
  return `${Math.min(4, Math.max(1, cols))}x${Math.min(5, Math.max(1, rows))}` as PanelSpan;
}

function layoutToSpan(layout: LayoutItem | undefined, fallback: PanelSpan): PanelSpan {
  if (!layout) {
    return fallback;
  }
  return formatSpan(layout.w, layout.h);
}

function createBreakpointLayout(items: DetailPanelGridItem[], cols: number): Layout {
  let cursorX = 0;
  let cursorY = 0;

  return items.map((item) => {
    const width = Math.min(cols, item.layout.w);
    const minWidth = Math.min(cols, item.layout.minW ?? 1);
    const maxWidth = Math.min(cols, item.layout.maxW ?? item.layout.w);

    if (cursorX + width > cols) {
      cursorX = 0;
      cursorY += 1;
    }

    const layout: LayoutItem = {
      i: item.id,
      x: cols === 1 ? 0 : Math.min(item.layout.x ?? cursorX, Math.max(0, cols - width)),
      y: cols === 1 ? cursorY : (item.layout.y ?? cursorY),
      w: width,
      h: item.layout.h,
      minW: minWidth,
      maxW: Math.max(minWidth, maxWidth),
      isResizable: item.layout.isResizable ?? false,
      ...(item.layout.minH === undefined ? {} : { minH: item.layout.minH }),
      ...(item.layout.maxH === undefined ? {} : { maxH: item.layout.maxH }),
    };

    cursorX = cols === 1 ? 0 : layout.x + layout.w;
    cursorY = cols === 1 ? layout.y + layout.h : Math.max(cursorY, layout.y);
    return layout;
  });
}

function createResponsiveCols(cols?: DetailPanelGridCols): Record<BreakpointName, number> {
  return {
    ...GRID_COLS,
    ...cols,
  };
}

function getBreakpointForWidth(width: number): BreakpointName {
  return (
    (Object.entries(GRID_BREAKPOINTS)
      .sort(([, firstWidth], [, secondWidth]) => secondWidth - firstWidth)
      .find(([, breakpointWidth]) => width >= breakpointWidth)?.[0] as
      BreakpointName | undefined) ?? 'xxs'
  );
}

function createLayouts(
  items: DetailPanelGridItem[],
  cols: Record<BreakpointName, number>
): DetailPanelLayouts {
  return {
    xxl: createBreakpointLayout(items, cols.xxl),
    lg: createBreakpointLayout(items, cols.lg),
    md: createBreakpointLayout(items, cols.md),
    sm: createBreakpointLayout(items, cols.sm),
    xs: createBreakpointLayout(items, cols.xs),
    xxs: createBreakpointLayout(items, cols.xxs),
  };
}

function estimateLayoutHeight(layout: Layout | undefined): number {
  const rowCount = layout?.reduce((height, item) => Math.max(height, item.y + item.h), 0) ?? 0;
  if (rowCount === 0) {
    return 0;
  }

  return rowCount * DETAIL_PANEL_GRID_ROW_HEIGHT + (rowCount - 1) * DETAIL_PANEL_GRID_MARGIN[1];
}

function createItemLayoutSignature(items: DetailPanelGridItem[] | undefined): string {
  return (
    items
      ?.map((item) =>
        [
          item.id,
          item.layout.x ?? '',
          item.layout.y ?? '',
          item.layout.w,
          item.layout.h,
          item.layout.minW ?? '',
          item.layout.maxW ?? '',
          item.layout.minH ?? '',
          item.layout.maxH ?? '',
          item.layout.isResizable ?? '',
        ].join(':')
      )
      .join('\u001f') ?? ''
  );
}

function createLayoutSignature(layout: Layout | undefined): string {
  return (
    layout
      ?.map((item) =>
        [
          item.i,
          item.x,
          item.y,
          item.w,
          item.h,
          item.minW ?? '',
          item.maxW ?? '',
          item.minH ?? '',
          item.maxH ?? '',
          item.isDraggable ?? '',
          item.isResizable ?? '',
        ].join(':')
      )
      .join('\u001f') ?? ''
  );
}

function findLayout(
  layouts: DetailPanelLayouts,
  itemId: string,
  currentLayout: Layout | undefined
): LayoutItem | undefined {
  const matchingLayouts = [
    currentLayout?.find((layout) => layout.i === itemId),
    layouts.xxl?.find((layout) => layout.i === itemId),
  ].filter((layout): layout is LayoutItem => Boolean(layout));

  return matchingLayouts.reduce<LayoutItem | undefined>((largest, layout) => {
    if (!largest) {
      return layout;
    }

    const largestArea = largest.w * largest.h;
    const layoutArea = layout.w * layout.h;
    if (layoutArea > largestArea || (layoutArea === largestArea && layout.w > largest.w)) {
      return layout;
    }

    return largest;
  }, undefined);
}

function clampLayoutDimension(
  value: number,
  minValue: number | undefined,
  maxValue: number | undefined
): number {
  const lowerBound = minValue ?? 1;
  const upperBound = maxValue ?? Number.MAX_SAFE_INTEGER;
  return Math.min(upperBound, Math.max(lowerBound, value));
}

function reconcileSavedLayoutItem(savedItem: LayoutItem, fallbackItem: LayoutItem): LayoutItem {
  const minW = fallbackItem.minW;
  const maxW = fallbackItem.maxW;
  const minH = fallbackItem.minH;
  const maxH = fallbackItem.maxH;

  const reconciled: LayoutItem = {
    ...savedItem,
    w: clampLayoutDimension(savedItem.w, minW, maxW),
    h: clampLayoutDimension(savedItem.h, minH, maxH),
    ...(minW === undefined ? {} : { minW }),
    ...(maxW === undefined ? {} : { maxW }),
    ...(minH === undefined ? {} : { minH }),
    ...(maxH === undefined ? {} : { maxH }),
    ...(fallbackItem.isResizable === undefined ? {} : { isResizable: fallbackItem.isResizable }),
    ...(fallbackItem.isDraggable === undefined ? {} : { isDraggable: fallbackItem.isDraggable }),
  };
  if (maxH === undefined) {
    delete reconciled.maxH;
  }
  return reconciled;
}

function normalizeSavedLayouts(
  savedLayouts: DetailPanelLayouts | undefined,
  fallbackLayouts: DetailPanelLayouts,
  visibleItemIds?: Set<string>
): DetailPanelLayouts {
  if (!savedLayouts) {
    return fallbackLayouts;
  }

  return Object.fromEntries(
    Object.entries(fallbackLayouts).map(([breakpoint, fallbackLayout]) => {
      const savedLayout = savedLayouts[breakpoint as BreakpointName] ?? [];
      const fallbackItemsById = new Map(fallbackLayout?.map((item) => [item.i, item]) ?? []);
      const fallbackIds = new Set(fallbackLayout?.map((item) => item.i) ?? []);
      const normalizedSavedLayout = savedLayout
        .filter(
          (item) =>
            fallbackIds.has(item.i) &&
            item.i !== ADD_TILE_ITEM_ID &&
            (!visibleItemIds || visibleItemIds.has(item.i))
        )
        .map((item) => {
          const fallbackItem = fallbackItemsById.get(item.i);
          return fallbackItem ? reconcileSavedLayoutItem(item, fallbackItem) : item;
        });
      const savedIds = new Set(normalizedSavedLayout.map((item) => item.i));
      const appendedFallbackLayout = fallbackLayout?.filter((item) => !savedIds.has(item.i)) ?? [];

      return [breakpoint, [...normalizedSavedLayout, ...appendedFallbackLayout]];
    })
  ) as DetailPanelLayouts;
}

function applyLayoutModeToLayouts(
  layouts: DetailPanelLayouts,
  items: DetailPanelGridItem[] | undefined,
  layoutMode: BackofficePageMode
): DetailPanelLayouts {
  const itemLayoutsById = new Map(items?.map((item) => [item.id, item.layout]) ?? []);
  const editingEnabled = layoutMode === 'edit';

  return Object.fromEntries(
    Object.entries(layouts).map(([breakpoint, layout]) => [
      breakpoint,
      layout?.map((layoutItem) => {
        const itemLayout = itemLayoutsById.get(layoutItem.i);
        return {
          ...layoutItem,
          isDraggable: editingEnabled,
          isResizable: editingEnabled && (itemLayout?.isResizable ?? false),
        };
      }),
    ])
  ) as DetailPanelLayouts;
}

function filterLayoutsForPersistence(
  layouts: DetailPanelLayouts,
  visibleItemIds: Set<string>
): DetailPanelLayouts {
  return Object.fromEntries(
    Object.entries(layouts).map(([breakpoint, layout]) => [
      breakpoint,
      layout?.filter((item) => item.i !== ADD_TILE_ITEM_ID && visibleItemIds.has(item.i)) ?? [],
    ])
  ) as DetailPanelLayouts;
}

export interface DetailPanelGridProps {
  children?: ReactNode;
  items?: DetailPanelGridItem[];
  resetKey?: string | number;
  cols?: DetailPanelGridCols | undefined;
  preferenceScope?: string | undefined;
  layoutMode?: BackofficePageMode | undefined;
  defaultVisibleItemIds?: string[] | undefined;
  defaultTileSettings?: Record<string, DetailPanelTileSettingsPreference> | undefined;
  tileCatalogLabel?: string | undefined;
  addTileLayout?: DetailPanelGridItemLayout | undefined;
}

export interface DetailPanelGridItem {
  id: string;
  title?: string | undefined;
  description?: string | undefined;
  layout: DetailPanelGridItemLayout;
  render: (layout: LayoutItem | undefined) => ReactNode;
}

export interface DetailPanelGridItemLayout {
  x?: number | undefined;
  y?: number | undefined;
  w: number;
  h: number;
  minW?: number | undefined;
  maxW?: number | undefined;
  minH?: number | undefined;
  maxH?: number | undefined;
  isResizable?: boolean | undefined;
}

export function panelSpanFromLayout(
  layout: LayoutItem | undefined,
  fallback: PanelSpan
): PanelSpan {
  return layoutToSpan(layout, fallback);
}

export function DetailPanelGrid({
  children,
  items,
  resetKey,
  cols,
  preferenceScope,
  layoutMode = 'view',
  defaultVisibleItemIds,
  defaultTileSettings,
  tileCatalogLabel = 'Add tile',
  addTileLayout,
}: DetailPanelGridProps) {
  const responsiveCols = useMemo(() => createResponsiveCols(cols), [cols]);
  const defaultPreference = useMemo<DetailPanelGridPreference>(
    () => ({
      ...(defaultTileSettings ? { tileSettings: defaultTileSettings } : {}),
    }),
    [defaultTileSettings]
  );
  const preference = useBackofficePreference(preferenceScope, {
    defaultValue: defaultPreference,
    debounceMs: 500,
  });
  const appliedPreferenceScopeRef = useRef<string | undefined>(undefined);
  const allItemIds = useMemo(() => items?.map((item) => item.id) ?? [], [items]);
  const allItemIdSet = useMemo(() => new Set(allItemIds), [allItemIds]);
  const defaultVisibleIds = useMemo(() => {
    const candidateIds = defaultVisibleItemIds ?? allItemIds;
    return candidateIds.filter((id) => allItemIdSet.has(id));
  }, [allItemIdSet, allItemIds, defaultVisibleItemIds]);
  const savedVisibleIds = Array.isArray(preference.value.visibleTileIds)
    ? preference.value.visibleTileIds.filter((id) => allItemIdSet.has(id))
    : undefined;
  const visibleTileIds = savedVisibleIds ?? defaultVisibleIds;
  const visibleTileIdSet = useMemo(() => new Set(visibleTileIds), [visibleTileIds]);
  const hiddenCatalogItems = useMemo(
    () => items?.filter((item) => !visibleTileIdSet.has(item.id)) ?? [],
    [items, visibleTileIdSet]
  );
  const addTileItem = useMemo<DetailPanelGridItem | null>(() => {
    if (!items || layoutMode !== 'edit') {
      return null;
    }

    return {
      id: ADD_TILE_ITEM_ID,
      title: tileCatalogLabel,
      layout: {
        x: Math.max(0, responsiveCols.xxl - (addTileLayout?.w ?? 1)),
        y: 9999,
        w: addTileLayout?.w ?? 1,
        h: addTileLayout?.h ?? 1,
        isResizable: false,
      },
      render: () => (
        <AddTilePanel
          label={tileCatalogLabel}
          availableItems={hiddenCatalogItems}
          onAddTile={(tileId) => {
            preference.setValue((current) => {
              const currentVisibleIds = Array.isArray(current.visibleTileIds)
                ? current.visibleTileIds.filter((id) => allItemIdSet.has(id))
                : undefined;
              const baseVisibleIds = currentVisibleIds ?? defaultVisibleIds;
              if (baseVisibleIds.includes(tileId)) {
                return current;
              }
              return {
                ...current,
                visibleTileIds: [...baseVisibleIds, tileId],
              };
            });
          }}
        />
      ),
    };
  }, [
    allItemIdSet,
    addTileLayout?.h,
    addTileLayout?.w,
    defaultVisibleIds,
    hiddenCatalogItems,
    items,
    layoutMode,
    preference,
    responsiveCols.xxl,
    tileCatalogLabel,
  ]);
  const visibleItems = useMemo(
    () => items?.filter((item) => visibleTileIdSet.has(item.id)) ?? [],
    [items, visibleTileIdSet]
  );
  const gridItems = useMemo(
    () => (addTileItem ? [...visibleItems, addTileItem] : visibleItems),
    [addTileItem, visibleItems]
  );
  const itemLayoutSignature = createItemLayoutSignature(gridItems);
  const colsSignature = Object.entries(responsiveCols)
    .map(([breakpoint, count]) => `${breakpoint}:${count}`)
    .join('\u001f');
  const resetSignature = `${resetKey ?? ''}\u001e${itemLayoutSignature}\u001e${colsSignature}`;
  const resetLayouts = useMemo(
    () => createLayouts(gridItems ?? [], responsiveCols),
    [gridItems, responsiveCols]
  );
  const [layoutState, setLayoutState] = useState<{
    signature: string;
    layouts: DetailPanelLayouts;
  }>(() => ({
    signature: resetSignature,
    layouts: resetLayouts,
  }));
  const [currentLayout, setCurrentLayout] = useState<Layout | undefined>(undefined);
  const liveLayoutRef = useRef<Layout | undefined>(undefined);
  const liveLayoutsRef = useRef<DetailPanelLayouts | undefined>(undefined);
  const currentLayoutSignatureRef = useRef('');
  const [interactionValues, setInteractionValues] = useState<DetailPanelInteractionValues>({});
  const interactionContextValue = useMemo(
    () => ({
      values: interactionValues,
      setValues: setInteractionValues,
      layoutMode,
      tileSettings: preference.value.tileSettings ?? {},
      setTileSettings: (tileId: string, settings: DetailPanelTileSettingsPreference) => {
        preference.setValue((current) => ({
          ...current,
          tileSettings: {
            ...(current.tileSettings ?? {}),
            [tileId]: settings,
          },
        }));
      },
      removeTile: (tileId: string) => {
        preference.setValue((current) => {
          const currentVisibleIds = Array.isArray(current.visibleTileIds)
            ? current.visibleTileIds.filter((id) => allItemIdSet.has(id))
            : undefined;
          const baseVisibleIds = currentVisibleIds ?? defaultVisibleIds;
          if (!baseVisibleIds.includes(tileId)) {
            return current;
          }
          return {
            ...current,
            visibleTileIds: baseVisibleIds.filter((id) => id !== tileId),
            layouts: current.layouts
              ? filterLayoutsForPersistence(
                  current.layouts,
                  new Set(baseVisibleIds.filter((id) => id !== tileId))
                )
              : current.layouts,
          };
        });
      },
    }),
    [allItemIdSet, defaultVisibleIds, interactionValues, layoutMode, preference]
  );
  const { width, mounted, containerRef } = useContainerWidth({
    initialWidth: 1280,
    measureBeforeMount: true,
  });
  const activeBreakpoint = getBreakpointForWidth(width);
  const layoutsAreStale = layoutState.signature !== resetSignature;
  const effectiveLayouts = layoutsAreStale ? resetLayouts : layoutState.layouts;
  const interactiveLayouts = useMemo(
    () => applyLayoutModeToLayouts(effectiveLayouts, items, layoutMode),
    [effectiveLayouts, items, layoutMode]
  );
  const effectiveCurrentLayout = layoutsAreStale ? undefined : currentLayout;

  const persistLayouts = useCallback(
    (layouts: DetailPanelLayouts) => {
      liveLayoutsRef.current = layouts;
      liveLayoutRef.current = layouts.xxl;
      setLayoutState({
        signature: resetSignature,
        layouts,
      });
      if (preferenceScope && preference.loaded) {
        const persistentLayouts = filterLayoutsForPersistence(layouts, visibleTileIdSet);
        preference.setValue((current) => ({
          ...current,
          layouts: persistentLayouts,
        }));
      }
    },
    [preference, preference.loaded, preferenceScope, resetSignature, visibleTileIdSet]
  );

  const persistActiveLayout = useCallback(
    (layout: Layout | undefined) => {
      persistLayouts({
        ...(liveLayoutsRef.current ?? interactiveLayouts),
        ...(layout ? { [activeBreakpoint]: layout } : {}),
      });
    },
    [activeBreakpoint, interactiveLayouts, persistLayouts]
  );

  function updateCurrentLayoutItem(itemId: string, width: number, height: number) {
    setCurrentLayout((previousLayout) => {
      const baseLayout = previousLayout ?? effectiveLayouts.xxl ?? [];
      const existingLayout = baseLayout.find((layout) => layout.i === itemId);
      const nextItem: LayoutItem = {
        ...(existingLayout ?? { i: itemId, x: 0, y: 0, w: width, h: height }),
        w: width,
        h: height,
      };

      const nextLayout = !existingLayout
        ? [...baseLayout, nextItem]
        : baseLayout.map((layout) => (layout.i === itemId ? nextItem : layout));
      liveLayoutRef.current = nextLayout;
      currentLayoutSignatureRef.current = createLayoutSignature(nextLayout);
      liveLayoutsRef.current = {
        ...effectiveLayouts,
        xxl: nextLayout,
      };
      return nextLayout;
    });
  }

  useEffect(() => {
    if (!layoutsAreStale) {
      return;
    }

    setLayoutState({
      signature: resetSignature,
      layouts: resetLayouts,
    });
    liveLayoutRef.current = undefined;
    liveLayoutsRef.current = undefined;
    currentLayoutSignatureRef.current = '';
    setCurrentLayout(undefined);
  }, [layoutsAreStale, resetLayouts, resetSignature]);

  useEffect(() => {
    appliedPreferenceScopeRef.current = undefined;
  }, [preferenceScope, resetSignature]);

  useEffect(() => {
    if (!preferenceScope || !preference.loaded) {
      return;
    }

    if (appliedPreferenceScopeRef.current === preferenceScope) {
      return;
    }

    appliedPreferenceScopeRef.current = preferenceScope;
    if (!preference.value.layouts) {
      return;
    }

    setLayoutState({
      signature: resetSignature,
      layouts: normalizeSavedLayouts(preference.value.layouts, resetLayouts, visibleTileIdSet),
    });
    liveLayoutRef.current = undefined;
    liveLayoutsRef.current = undefined;
    currentLayoutSignatureRef.current = '';
    setCurrentLayout(undefined);
  }, [
    preference.loaded,
    preference.value.layouts,
    preferenceScope,
    resetLayouts,
    resetSignature,
    visibleTileIdSet,
  ]);

  if (items) {
    return (
      <DetailPanelInteractionContext.Provider value={interactionContextValue}>
        <div ref={containerRef as Ref<HTMLDivElement>}>
          {mounted ? (
            <ResponsiveGridLayout
              className="detail-panel-grid detail-panel-grid--stable"
              width={width}
              layouts={interactiveLayouts}
              breakpoints={GRID_BREAKPOINTS}
              cols={responsiveCols}
              rowHeight={DETAIL_PANEL_GRID_ROW_HEIGHT}
              margin={DETAIL_PANEL_GRID_MARGIN}
              containerPadding={[0, 0]}
              resizeConfig={{ enabled: layoutMode === 'edit', handles: ['se'] }}
              dragConfig={{
                enabled: layoutMode === 'edit',
                bounded: true,
                handle: '.tile-drag-header',
                cancel:
                  "a,button,input,textarea,select,[role='dialog'],.tile-settings-modal,.no-tile-drag",
                threshold: 8,
              }}
              onLayoutChange={(layout: Layout, allLayouts: DetailPanelLayouts) => {
                const nextLayoutSignature = createLayoutSignature(layout);
                liveLayoutRef.current = layout;
                liveLayoutsRef.current = allLayouts;
                if (currentLayoutSignatureRef.current !== nextLayoutSignature) {
                  currentLayoutSignatureRef.current = nextLayoutSignature;
                  setCurrentLayout(layout);
                }
              }}
              onDragStop={(layout: Layout | undefined) => {
                persistActiveLayout(layout);
              }}
              onResize={(_layout, _oldItem, newItem) => {
                if (newItem) {
                  updateCurrentLayoutItem(newItem.i, newItem.w, newItem.h);
                }
              }}
              onResizeStop={(layout, _oldItem, newItem) => {
                if (newItem) {
                  updateCurrentLayoutItem(newItem.i, newItem.w, newItem.h);
                }
                persistActiveLayout(layout);
              }}
            >
              {gridItems.map((item) => (
                <div key={item.id}>
                  {item.render(findLayout(interactiveLayouts, item.id, effectiveCurrentLayout))}
                </div>
              ))}
            </ResponsiveGridLayout>
          ) : (
            <div
              aria-hidden="true"
              data-testid="detail-panel-grid-measuring"
              style={{ minHeight: estimateLayoutHeight(effectiveLayouts.xxl) }}
            />
          )}
        </div>
      </DetailPanelInteractionContext.Provider>
    );
  }

  return (
    <DetailPanelInteractionContext.Provider value={interactionContextValue}>
      <div className="grid auto-rows-[minmax(12rem,auto)] grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6">
        {children}
      </div>
    </DetailPanelInteractionContext.Provider>
  );
}

export interface DetailPanelProps {
  title: string;
  span: PanelSpan;
  children?: ReactNode;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
  className?: string | undefined;
  contentClassName?: string | undefined;
  contentOverflow?: 'auto' | 'hidden' | undefined;
  settingsItems?: DetailPanelSettingsItem[] | undefined;
  settingsContent?: ReactNode | undefined;
  headerActions?: ReactNode | undefined;
}

export interface DetailPanelSettingsItem {
  id: string;
  label: string;
  content: ReactNode;
}

function applySavedSettingsOrder(currentIds: string[], savedIds: string[] | undefined): string[] {
  if (!savedIds?.length) {
    return currentIds;
  }

  const currentIdSet = new Set(currentIds);
  const orderedIds = savedIds.filter((id) => currentIdSet.has(id));
  const appendedIds = currentIds.filter((id) => !orderedIds.includes(id));
  return [...orderedIds, ...appendedIds];
}

export function DetailPanel({
  title,
  span,
  children,
  panelId,
  currentSpan,
  className,
  contentClassName,
  contentOverflow,
  settingsItems,
  settingsContent,
  headerActions,
}: DetailPanelProps) {
  return (
    <DetailPanelShell
      title={title}
      span={currentSpan ?? span}
      panelId={panelId}
      className={className}
      contentClassName={contentClassName}
      contentOverflow={contentOverflow}
      settingsItems={settingsItems}
      settingsContent={settingsContent}
      headerActions={headerActions}
    >
      {children}
    </DetailPanelShell>
  );
}

interface DetailPanelShellProps {
  title: string;
  span: PanelSpan;
  children?: ReactNode;
  panelId?: string | undefined;
  style?: CSSProperties | undefined;
  isDragging?: boolean | undefined;
  className?: string | undefined;
  contentClassName?: string | undefined;
  contentOverflow?: 'auto' | 'hidden' | undefined;
  settingsItems?: DetailPanelSettingsItem[] | undefined;
  settingsContent?: ReactNode | undefined;
  headerActions?: ReactNode | undefined;
}

function DetailPanelShell({
  title,
  span,
  children,
  panelId,
  style,
  isDragging = false,
  className,
  contentClassName,
  contentOverflow,
  settingsItems,
  settingsContent,
  headerActions,
}: DetailPanelShellProps) {
  const interactionContext = useContext(DetailPanelInteractionContext);
  const layoutMode = interactionContext?.layoutMode ?? 'view';
  const contentRef = useRef<HTMLDivElement | null>(null);
  const headingId = `panel-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const settingsIdSignature = settingsItems?.map((item) => item.id).join('\u001f') ?? '';
  const savedTileSettings = panelId ? interactionContext?.tileSettings[panelId] : undefined;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [orderedSettingsIds, setOrderedSettingsIds] = useState(() =>
    applySavedSettingsOrder(
      settingsItems?.map((item) => item.id) ?? [],
      savedTileSettings?.orderedSettingIds
    )
  );
  const [hiddenSettingsIds, setHiddenSettingsIds] = useState<Set<string>>(
    () => new Set(savedTileSettings?.hiddenSettingIds ?? [])
  );
  const orderedSettingsIdsRef = useRef(orderedSettingsIds);
  const hiddenSettingsIdsRef = useRef(hiddenSettingsIds);
  const settingsById = new Map(settingsItems?.map((item) => [item.id, item]) ?? []);
  const visibleSettingsItems = orderedSettingsIds
    .filter((id) => !hiddenSettingsIds.has(id))
    .map((id) => settingsById.get(id))
    .filter((item): item is DetailPanelSettingsItem => Boolean(item));
  const isCompactSquare = span === '1x1';
  const shouldHideBodyOverflow = isCompactSquare || contentOverflow === 'hidden';
  const hasSettings = Boolean(settingsItems || settingsContent);
  const hasHeaderActions = layoutMode === 'view' && Boolean(headerActions || hasSettings);
  const canRemoveTile = layoutMode === 'edit' && Boolean(panelId);

  useEffect(() => {
    const currentIds = settingsItems?.map((item) => item.id) ?? [];
    const validIds = new Set(currentIds);
    const nextOrderedIds = applySavedSettingsOrder(
      currentIds,
      savedTileSettings?.orderedSettingIds
    );
    const nextHiddenIds = new Set(
      (savedTileSettings?.hiddenSettingIds ?? []).filter((id) => validIds.has(id))
    );
    orderedSettingsIdsRef.current = nextOrderedIds;
    hiddenSettingsIdsRef.current = nextHiddenIds;
    setOrderedSettingsIds(nextOrderedIds);
    setHiddenSettingsIds(nextHiddenIds);
  }, [
    savedTileSettings?.hiddenSettingIds,
    savedTileSettings?.orderedSettingIds,
    settingsIdSignature,
    settingsItems,
  ]);

  function persistTileSettings(nextOrderedIds: string[], nextHiddenIds: Set<string>) {
    if (!panelId) {
      return;
    }

    interactionContext?.setTileSettings(panelId, {
      orderedSettingIds: nextOrderedIds,
      hiddenSettingIds: Array.from(nextHiddenIds),
    });
  }

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) {
      return;
    }

    if (layoutMode === 'edit') {
      contentElement.setAttribute('inert', '');
    } else {
      contentElement.removeAttribute('inert');
    }

    return () => contentElement.removeAttribute('inert');
  }, [layoutMode]);

  function handleSettingsDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const currentIds = orderedSettingsIdsRef.current;
    const oldIndex = currentIds.indexOf(String(active.id));
    const newIndex = currentIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const nextIds = arrayMove(currentIds, oldIndex, newIndex);
    orderedSettingsIdsRef.current = nextIds;
    setOrderedSettingsIds(nextIds);
    persistTileSettings(nextIds, hiddenSettingsIdsRef.current);
  }

  function toggleSetting(settingId: string) {
    const nextIds = new Set(hiddenSettingsIdsRef.current);
    if (nextIds.has(settingId)) {
      nextIds.delete(settingId);
    } else {
      nextIds.add(settingId);
    }

    hiddenSettingsIdsRef.current = nextIds;
    setHiddenSettingsIds(nextIds);
    persistTileSettings(orderedSettingsIdsRef.current, nextIds);
  }

  return (
    <>
      <section
        style={style}
        className={[
          'group/tile relative flex h-full flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white p-5 shadow-sm',
          isDragging ? 'shadow-lg ring-2 ring-lavender-200' : '',
          layoutMode === 'edit' ? 'detail-panel-tile--edit-mode' : '',
          PANEL_SPAN_CLASSES[span],
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        data-panel-span={span}
        aria-labelledby={headingId}
      >
        <div
          className={[
            'tile-drag-header group/header relative -mx-5 -mt-5 flex items-center rounded-t-lg px-5',
            layoutMode === 'edit' ? 'cursor-grab active:cursor-grabbing' : '',
            isCompactSquare ? 'min-h-12 py-3' : 'min-h-16 py-4',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className="tile-header-overlay pointer-events-none absolute inset-0 z-10 rounded-t-lg bg-white opacity-0 transition-opacity group-hover/header:opacity-50 group-focus-within/header:opacity-50"
            aria-hidden="true"
          />
          <h2
            id={headingId}
            className={[
              'min-w-0 flex-1 font-inter-tight text-xl font-semibold text-neutral-900',
              isCompactSquare ? 'text-center' : 'text-left',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {title}
          </h2>
          {hasHeaderActions ? (
            <div
              className="absolute right-5 top-4 z-20 flex items-center gap-1 opacity-0 transition-opacity group-hover/header:opacity-100 group-focus-within/header:opacity-100"
              data-tile-header-actions=""
            >
              {headerActions}
              {hasSettings ? (
                <button
                  type="button"
                  className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 transition hover:bg-backoffice-50 hover:text-neutral-700"
                  aria-label={`Open ${title} tile settings`}
                  title="Settings"
                  onClick={() => setSettingsOpen(true)}
                >
                  <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ) : null}
          {canRemoveTile ? (
            <button
              type="button"
              className="focus-ring absolute right-5 top-4 z-20 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white text-neutral-500 shadow-sm ring-1 ring-neutral-200 transition hover:bg-red-50 hover:text-red-700"
              aria-label={`Remove ${title} tile`}
              title="Remove tile"
              onClick={() => {
                if (panelId) {
                  interactionContext?.removeTile(panelId);
                }
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div
          ref={contentRef}
          data-panel-content=""
          className={[
            isCompactSquare ? 'mt-0' : 'mt-5',
            'relative min-h-0 flex-1 overflow-x-hidden',
            shouldHideBodyOverflow ? 'overflow-y-hidden' : 'overflow-y-auto',
            contentClassName,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {settingsItems ? (
            <dl className="text-body">{visibleSettingsItems.map((item) => item.content)}</dl>
          ) : (
            children
          )}
          {layoutMode === 'edit' ? (
            <div
              className="absolute inset-0 z-30 cursor-move bg-transparent"
              data-testid="detail-panel-edit-mode-interaction-shield"
              aria-hidden="true"
            />
          ) : null}
        </div>
      </section>
      {settingsOpen && settingsItems ? (
        <TileSettingsModal
          title={title}
          orderedIds={orderedSettingsIds}
          hiddenIds={hiddenSettingsIds}
          itemsById={settingsById}
          onClose={() => setSettingsOpen(false)}
          onDragEnd={handleSettingsDragEnd}
          onToggle={toggleSetting}
        />
      ) : settingsOpen && settingsContent ? (
        <TileCustomSettingsModal title={title} onClose={() => setSettingsOpen(false)}>
          {settingsContent}
        </TileCustomSettingsModal>
      ) : null}
    </>
  );
}

interface AddTilePanelProps {
  label: string;
  availableItems: DetailPanelGridItem[];
  onAddTile: (tileId: string) => void;
}

function AddTilePanel({ label, availableItems, onAddTile }: AddTilePanelProps) {
  const [catalogOpen, setCatalogOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="focus-ring flex h-full min-h-44 w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-neutral-300 bg-white p-5 text-neutral-500 shadow-sm transition hover:border-backoffice-300 hover:bg-backoffice-50 hover:text-backoffice-700"
        aria-label={label}
        onClick={() => setCatalogOpen(true)}
      >
        <Plus className="h-10 w-10" aria-hidden="true" />
        <span className="text-small font-semibold">{label}</span>
      </button>
      {catalogOpen ? (
        <TileCatalogModal
          title={label}
          availableItems={availableItems}
          onClose={() => setCatalogOpen(false)}
          onAddTile={(tileId) => {
            onAddTile(tileId);
            setCatalogOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

interface TileCatalogModalProps {
  title: string;
  availableItems: DetailPanelGridItem[];
  onClose: () => void;
  onAddTile: (tileId: string) => void;
}

function TileCatalogModal({ title, availableItems, onClose, onAddTile }: TileCatalogModalProps) {
  return createPortal(
    <div
      className="tile-settings-modal fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-neutral-950/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tile-catalog-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h2 id="tile-catalog-title" className="font-inter-tight text-xl font-semibold">
            {title}
          </h2>
          <button
            type="button"
            className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 hover:bg-backoffice-50"
            aria-label="Close tile catalog"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-5">
          {availableItems.length === 0 ? (
            <p className="rounded-md border border-neutral-200 bg-neutral-50 px-4 py-6 text-center text-body text-neutral-500">
              All tiles are already on this tab.
            </p>
          ) : (
            availableItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className="focus-ring block w-full rounded-md border border-neutral-200 bg-white px-4 py-3 text-left transition hover:border-backoffice-200 hover:bg-backoffice-50"
                onClick={() => onAddTile(item.id)}
              >
                <span className="block text-body font-semibold text-neutral-900">
                  {item.title ?? item.id}
                </span>
                {item.description ? (
                  <span className="mt-1 block text-small text-neutral-500">{item.description}</span>
                ) : null}
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

interface TileSettingsDialogProps {
  title: string;
  children: ReactNode;
  onClose: () => void;
}

function TileSettingsDialog({ title, children, onClose }: TileSettingsDialogProps) {
  return createPortal(
    <div
      className="tile-settings-modal fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-neutral-950/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tile-settings-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <div>
            <h2 id="tile-settings-title" className="font-inter-tight text-xl font-semibold">
              Tile Settings
            </h2>
            <p className="text-small text-neutral-500">{title}</p>
          </div>
          <button
            type="button"
            className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 hover:bg-backoffice-50"
            aria-label="Close tile settings"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function TileCustomSettingsModal({ title, children, onClose }: TileSettingsDialogProps) {
  return (
    <TileSettingsDialog title={title} onClose={onClose}>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">{children}</div>
    </TileSettingsDialog>
  );
}

interface TileSettingsModalProps {
  title: string;
  orderedIds: string[];
  hiddenIds: Set<string>;
  itemsById: Map<string, DetailPanelSettingsItem>;
  onClose: () => void;
  onDragEnd: (event: DragEndEvent) => void;
  onToggle: (settingId: string) => void;
}

function TileSettingsModal({
  title,
  orderedIds,
  hiddenIds,
  itemsById,
  onClose,
  onDragEnd,
  onToggle,
}: TileSettingsModalProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <TileSettingsDialog title={title} onClose={onClose}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-5">
            {orderedIds.map((id) => {
              const item = itemsById.get(id);
              if (!item) {
                return null;
              }
              return (
                <SortableSettingsItem
                  key={id}
                  id={id}
                  label={item.label}
                  checked={!hiddenIds.has(id)}
                  onToggle={() => onToggle(id)}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </TileSettingsDialog>
  );
}

interface SortableSettingsItemProps {
  id: string;
  label: string;
  checked: boolean;
  onToggle: () => void;
}

function SortableSettingsItem({ id, label, checked, onToggle }: SortableSettingsItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={[
        'flex items-center gap-3 rounded-md border border-neutral-200 px-3 py-2',
        checked ? 'bg-white text-neutral-900' : 'bg-neutral-50 text-neutral-400',
        isDragging ? 'shadow-md ring-2 ring-lavender-200' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="focus-ring inline-flex h-7 w-7 cursor-grab items-center justify-center rounded-md text-neutral-400 hover:bg-backoffice-50 hover:text-neutral-700 active:cursor-grabbing"
        aria-label={`Move ${label}`}
        title="Move"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="min-w-0 flex-1 text-body font-semibold">{label}</span>
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-neutral-300 text-lavender-700"
        aria-label={`Show ${label}`}
        checked={checked}
        onChange={onToggle}
      />
    </div>
  );
}
